import axios from 'axios';
import type {
  Pool,
  PoolPage,
  GlobalRankingEntry,
  GlobalRankingPage,
  CreatePoolRequest,
  JoinPoolRequest,
  Match,
  CreateMatchRequest,
  BulkCreateMatchesRequest,
  Prediction,
  PredictionPage,
  UpsertPredictionRequest,
  Standing,
  FinalizeMatchRequest,
  RegisterRequest,
  LoginRequest,
  LoginResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  User,
  UserPage,
  UserStats,
  Competition,
  CompetitionRelationships,
  Country,
  FootballApiLeague,
  ActiveCountry,
  CreateActiveCountryRequest,
  Team,
  TeamPlayer,
  VersionResponse,
  Invitation,
  PhaseSummaryResponse,
  PhaseMapping,
  UpdatePhaseMappingRequest,
  BannersResponse,
  ValidityOptions,
  SimulatorBracket,
} from '../types';
import { safeStorage } from '../utils/safeStorage';
import { LANG_COOKIE_NAME } from '../i18n';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    // Lido pelo backend (ClientPlatformObservationConfig) e adicionado como
    // tag `client_platform` em todas as métricas http_server_requests_*.
    // Permite separar mobile vs web nos dashboards/alertas do Grafana (13.6).
    'X-Client-Platform': 'web',
  },
});

// Interceptor para adicionar token JWT automaticamente
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Release C: idioma corrente. Envia tanto via Accept-Language quanto via
  // query param ?lang= porque o CloudFront das distribuições atuais NÃO
  // encaminha o header Accept-Language pra origem (cache policy não inclui).
  // O LocaleFilter do backend lê ?lang= primeiro, então isso garante que
  // o idioma chega independente do header. Quando a origin request policy
  // for atualizada (Terraform) pra incluir Accept-Language, o header passa
  // a chegar também — sem regressão (ambos resolvem pro mesmo valor).
  //
  // Lê primeiro o cookie (preferido — cross-subdomain) e fallback pra
  // localStorage (host-only). Nomes vêm da config build-time.
  const lang =
    readLangCookie() ||
    safeStorage.getItem(LANG_COOKIE_NAME) ||
    safeStorage.getItem('bolao-lang');
  if (lang) {
    config.headers['Accept-Language'] = lang;
    // Adiciona ?lang= sem sobrescrever params existentes.
    config.params = { ...(config.params || {}), lang };
  }
  return config;
});

/** Lê o cookie de idioma (ex: bolao-lang-dev em DEV, bolao-lang em PROD). */
function readLangCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const name = `${LANG_COOKIE_NAME}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(name)) return decodeURIComponent(trimmed.slice(name.length));
  }
  return null;
}

// -------------------------------------------------------------------
// Refresh token: estado compartilhado para evitar múltiplas chamadas
// paralelas ao /auth/refresh quando várias requests expiram ao mesmo tempo
// -------------------------------------------------------------------
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
}

function forceLogout() {
  safeStorage.removeItem('token');
  safeStorage.removeItem('refreshToken');
  safeStorage.removeItem('user');
  import('react-hot-toast').then(({ default: toast }) => {
    toast.error('Sessão expirada. Faça login novamente.', { duration: 4000 });
  });
  setTimeout(() => {
    window.location.href = '/auth';
  }, 1500);
  return Promise.reject(new Error('Sessão expirada'));
}

// Interceptor para tratar erros de autenticação com renovação automática
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url: string = originalRequest?.url || '';

    // Não interceptar endpoints de auth (evita loop infinito)
    const isAuthEndpoint = url.includes('/auth/');

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      const refreshToken = safeStorage.getItem('refreshToken');

      // Sem refresh token → logout direto
      if (!refreshToken) {
        return forceLogout();
      }

      // Já está renovando: enfileirar essa request para repetir depois
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Iniciar renovação
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        const newToken: string = data.token;
        safeStorage.setItem('token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError: any) {
        // Só encerra a sessão quando o backend confirma que o refresh token é inválido
        // (401 = token revogado/não encontrado, 400 = token ausente/inválido)
        // Para erros de rede (sem response) ou 5xx, propaga sem deslogar
        const refreshStatus = refreshError?.response?.status;
        if (refreshStatus === 401 || refreshStatus === 400) {
          processQueue(new Error('Sessão expirada'), null);
          return forceLogout();
        }
        // Erro transitório (rede, 5xx) — rejeita mas mantém a sessão ativa
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 403 = sem permissão, não deslogar — deixa o erro ser tratado pelo caller
    return Promise.reject(error);
  }
);

// Pools
export const poolsApi = {
  list: () => api.get<Pool[]>('/pools').then((res) => res.data),
  listByUser: (userExternalId: string) => api.get<Pool[]>(`/pools/user/${userExternalId}`).then((res) => res.data),
  listCreatedByUser: (userExternalId: string) => api.get<Pool[]>(`/pools/created-by/${userExternalId}`).then((res) => res.data),
  getById: (id: number) => api.get<Pool>(`/pools/${id}`).then((res) => res.data),
  create: (data: CreatePoolRequest) => api.post<Pool>('/pools', data).then((res) => res.data),
  join: (data: JoinPoolRequest) => api.post<Pool>('/pools/join', data).then((res) => res.data),
  delete: (id: number) => api.delete(`/pools/${id}`).then((res) => res.data),
  listPublic: (page = 0, size = 20, userExternalId?: string) => {
    const params: Record<string, string | number> = { page, size };
    if (userExternalId) params.userExternalId = userExternalId;
    return api.get<PoolPage>('/pools/public', { params }).then((res) => res.data);
  },
  joinPublic: (poolId: number, userExternalId: string) =>
    api.post<Pool>(`/pools/${poolId}/join-public`, { userExternalId }).then((res) => res.data),
  leave: (poolId: number, userExternalId: string) =>
    api.delete(`/pools/${poolId}/leave`, { params: { userExternalId } }).then((res) => res.data),
  // Release pré-PROD: criador/admin gerencia membros do bolão.
  getMembers: (poolId: number) =>
    api.get<import('../types').PoolMember[]>(`/pools/${poolId}/members`).then((res) => res.data),
  removeMember: (poolId: number, targetExternalId: string) =>
    api.delete(`/pools/${poolId}/members/${targetExternalId}`).then((res) => res.data),
  finish: (poolId: number) =>
    api.patch<Pool>(`/pools/${poolId}/finish`).then((res) => res.data),
  rename: (poolId: number, name: string) =>
    api.patch<Pool>(`/pools/${poolId}/name`, { name }).then((res) => res.data),
  getPublicCompetitions: () =>
    api.get<{ id: number; name: string }[]>('/pools/public/competitions').then((res) => res.data),
  listPinned: () =>
    api.get<Pool[]>('/pools/pinned').then((res) => res.data),
  togglePin: (poolId: number) =>
    api.patch<Pool>(`/pools/${poolId}/pin`).then((res) => res.data),
  toggleHidePredictions: (poolId: number) =>
    api.patch<Pool>(`/pools/${poolId}/hide-predictions`).then((res) => res.data),
  togglePalpitometro: (poolId: number) =>
    api.patch<Pool>(`/pools/${poolId}/toggle-palpitometro`).then((res) => res.data),
};

// Matches
export const matchesApi = {
  listByPool: (poolId: number) => api.get<Match[]>(`/matches/pool/${poolId}`).then((res) => res.data),
  listByCompetition: (competitionId: number, phase?: string, roundNumber?: number, poolId?: number) => {
    const params: any = {};
    if (phase) params.phase = phase;
    if (roundNumber !== undefined) params.roundNumber = roundNumber;
    if (poolId !== undefined) params.poolId = poolId;
    return api.get<Match[]>(`/matches/competition/${competitionId}`, { params }).then((res) => res.data);
  },
  getPhasesByCompetition: (competitionId: number) =>
    api.get<string[]>(`/matches/competition/${competitionId}/phases`).then((res) => res.data),
  getRoundsByPhase: (competitionId: number, phase: string) =>
    api.get<number[]>(`/matches/competition/${competitionId}/phases/${encodeURIComponent(phase)}/rounds`).then((res) => res.data),
  getCurrentRound: (competitionId: number) => api.get<number>(`/matches/competition/${competitionId}/current-round`).then((res) => res.data),
  getPhaseSummary: (competitionId: number, phaseCode: string, roundNumber?: number, poolId?: number) => {
    const params: Record<string, number> = {};
    if (roundNumber !== undefined) params.roundNumber = roundNumber;
    if (poolId !== undefined) params.poolId = poolId;
    return api.get<PhaseSummaryResponse>(
      `/matches/competition/${competitionId}/phases/${encodeURIComponent(phaseCode)}/summary`,
      { params }
    ).then((res) => res.data);
  },
  getUpcoming: (competitionId?: number) => {
    const params: Record<string, number> = {};
    if (competitionId !== undefined) params.competitionId = competitionId;
    return api.get<Match[]>('/matches/upcoming', { params }).then((res) => res.data);
  },
  // Hub "Jogos de Hoje": jogos do período (range ISO local) de todos os bolões do
  // usuário. userExternalId vem do JWT no backend; aqui só enviamos start/end.
  getTodayAcrossPools: (start: string, end: string) =>
    api.get<import('../types').TodayAcrossPoolsResponse>('/matches/today-across-pools', { params: { start, end } }).then((res) => res.data),
  getById: (id: number) => api.get<Match>(`/matches/${id}`).then((res) => res.data),
  create: (data: CreateMatchRequest) => api.post<Match>('/matches', data).then((res) => res.data),
  bulkCreate: (data: BulkCreateMatchesRequest) =>
    api.post('/matches/bulk', data).then((res) => res.data),
  update: (id: number, data: CreateMatchRequest) => api.put<Match>(`/matches/${id}`, data).then((res) => res.data),
  finalize: (id: number, data: FinalizeMatchRequest) =>
    api.post<Match>(`/matches/${id}/finalize`, data).then((res) => res.data),
  delete: (id: number) => api.delete(`/matches/${id}`).then((res) => res.data),
};

// Predictions
export const predictionsApi = {
  getByUserAndPool: (userExternalId: string, poolId: number) =>
    api.get<Prediction[]>(`/predictions/user/${userExternalId}/pool/${poolId}`).then((res) => res.data),
  getByPoolAndMatch: (poolId: number, matchId: number, page = 0, size = 50) =>
    api.get<PredictionPage>(`/predictions/pool/${poolId}/match/${matchId}`, { params: { page, size } }).then((res) => res.data),
  upsert: (data: UpsertPredictionRequest) =>
    api.post<Prediction>('/predictions', data).then((res) => res.data),
  bulkUpsert: (data: UpsertPredictionRequest[]) =>
    api.post<Prediction[]>('/predictions/bulk', data).then((res) => res.data),
};

// Standings
export const standingsApi = {
  getByPool: (poolId: number, page = 0, size = 10) =>
    api.get<import('../types').StandingPage>(`/standings/pool/${poolId}`, { params: { page, size } }).then((res) => res.data),
  compute: (poolId: number) =>
    api.post<Standing[]>(`/standings/pool/${poolId}/compute`).then((res) => res.data),
};

// Rankings globais e por competição
export const rankingsApi = {
  getGlobal: (page = 0, size = 50, userExternalId?: string, competitionId?: number) => {
    const params: Record<string, string | number> = { page, size };
    if (userExternalId) params.userExternalId = userExternalId;
    if (competitionId) params.competitionId = competitionId;
    return api.get<GlobalRankingPage>('/rankings/global', { params }).then((res) => res.data);
  },
  getMyGlobalPosition: (userExternalId: string, competitionId?: number) => {
    const params: Record<string, string | number> = { userExternalId };
    if (competitionId) params.competitionId = competitionId;
    return api.get<GlobalRankingEntry>(`/rankings/global/me`, { params }).then((res) => res.data);
  },
  getByCompetition: (competitionId: number, season: number, page = 0, size = 50, userExternalId?: string) => {
    const params: Record<string, string | number> = { season, page, size };
    if (userExternalId) params.userExternalId = userExternalId;
    return api.get<GlobalRankingPage>(`/rankings/competition/${competitionId}`, { params }).then((res) => res.data);
  },
};

// Health
export const healthApi = {
  check: () => api.get('/health').then((res) => res.data),
};

// Version
export const versionApi = {
  get: () => api.get<VersionResponse>('/version').then((res) => res.data),
};

// Auth
export const authApi = {
  register: (data: RegisterRequest) => api.post<User>('/auth/register', data).then((res) => res.data),
  login: (data: LoginRequest) => api.post<LoginResponse>('/auth/login', data).then((res) => res.data),
  googleLogin: (idToken: string) => api.post<LoginResponse>('/auth/google', { idToken }).then((res) => res.data),
  forgotPassword: (data: ForgotPasswordRequest) => api.post('/auth/forgot-password', data).then((res) => res.data),
  resetPassword: (data: ResetPasswordRequest) => api.post('/auth/reset-password', data).then((res) => res.data),
  changePassword: (data: ChangePasswordRequest) => api.post('/auth/change-password', data).then((res) => res.data),
  verifyEmail: (token: string) => api.get('/auth/verify-email', { params: { token } }).then((res) => res.data),
  resendVerification: (email: string) => api.post('/auth/resend-verification', { email }).then((res) => res.data),
  logout: (refreshToken: string) => api.post('/auth/logout', { refreshToken }).then((res) => res.data),
  checkEmail: (email: string) => api.post<{ exists: boolean }>('/auth/check-email', { email }).then((res) => res.data),
  initiateRegistration: (email: string) => api.post('/auth/initiate-registration', { email }).then((res) => res.data),
  completeRegistration: (data: { token: string; name: string; password: string }) =>
    api.post<LoginResponse>('/auth/complete-registration', data).then((res) => res.data),
};

// System Config (SUPER_ADMIN)
export const configApi = {
  getAll: () => api.get<Record<string, string>>('/admin/config').then((res) => res.data),
  saveAll: (configs: Record<string, string>) => api.put('/admin/config', configs),
  getPublic: () =>
    api.get<{ googleVerificationCode: string; googleAnalyticsId: string; googleClientId: string }>(
      '/admin/config/public'
    ).then((res) => res.data),
  getFreeLimits: () =>
    api.get<{ maxPrivatePoolsCreated: number; maxPrivatePoolMembers: number; maxPrivatePoolsJoined: number }>(
      '/admin/config/free-limits'
    ).then((res) => res.data),
  /** Versão pública (sem token) para uso em landing pages não autenticadas */
  getPublicFreeLimits: () =>
    axios.get<import('../types').FreeLimits>(`${BASE_URL}/admin/config/free-limits`).then((res) => res.data),
};

// i18n / traduções (Release C)
export const i18nAdminApi = {
  getBundle: (locale: string) =>
    api.get<Record<string, Record<string, string>>>(`/i18n/${locale}`).then((r) => r.data),
  upsert: (locale: string, namespace: string, key: string, value: string) =>
    api.patch(
      `/admin/i18n/${locale}/${namespace}/${encodeURIComponent(key)}`,
      { value },
    ),
  bulkImport: (locale: string, namespace: string, entries: Record<string, string>) =>
    api.post('/admin/i18n/import', { locale, namespace, entries }),
  exportLocale: (locale: string) =>
    api
      .get<Record<string, Record<string, string>>>('/admin/i18n/export', {
        params: { locale },
      })
      .then((r) => r.data),
  missing: (target: string, reference = 'pt-BR') =>
    api
      .get<Array<{ namespace: string; key: string; referenceValue: string }>>(
        '/admin/i18n/missing',
        { params: { target, reference } },
      )
      .then((r) => r.data),
};

// Competitions
export const competitionsApi = {
  list: () => api.get<Competition[]>('/competitions').then((res) => res.data),
  listAll: () => api.get<Competition[]>('/competitions/all').then((res) => res.data),
  getById: (id: number) => api.get<Competition>(`/competitions/${id}`).then((res) => res.data),
  getRelationships: (id: number) => api.get<CompetitionRelationships>(`/competitions/${id}/relationships`).then((res) => res.data),
  create: (data: Omit<Competition, 'id'>) => api.post<Competition>('/competitions', data).then((res) => res.data),
  update: (id: number, data: Omit<Competition, 'id'>) => api.put<Competition>(`/competitions/${id}`, data).then((res) => res.data),
  delete: (id: number) => api.delete(`/competitions/${id}`).then((res) => res.data),
  syncFromApi: (country: string) => api.post<{ count: number; message: string; success: boolean }>(`/competitions/sync?country=${country}`).then((res) => res.data),
  syncMatches: (id: number) => api.post<{ count: number; message: string; success: boolean }>(`/competitions/${id}/sync-matches`).then((res) => res.data),
  getCountriesWithActiveLeagues: () => api.get<Country[]>('/competitions/catalog/countries').then((res) => res.data),
  getActiveLeaguesByCountry: (countryCode: string) => api.get<FootballApiLeague[]>(`/competitions/catalog/leagues/${countryCode}`).then((res) => res.data),
  getPhaseMappings: (id: number, season: number) =>
    api.get<PhaseMapping[]>(`/competitions/${id}/phases/mappings?season=${season}`).then((res) => res.data),
  updatePhaseMapping: (id: number, mappingId: number, data: UpdatePhaseMappingRequest) =>
    api.put<PhaseMapping>(`/competitions/${id}/phases/mappings/${mappingId}`, data).then((res) => res.data),
  syncPhaseMappings: (id: number) =>
    api.post<{ success: boolean; message: string }>(`/competitions/${id}/phases/sync`).then((res) => res.data),
  getValidityOptions: (id: number) =>
    api.get<ValidityOptions>(`/competitions/${id}/validity-options`).then((res) => res.data),
  getPhaseMatches: (id: number, phaseCode: string, groupCode?: string, roundNumber?: number) => {
    let url = `/competitions/${id}/phase-matches?phaseCode=${encodeURIComponent(phaseCode)}`;
    if (groupCode) url += `&groupCode=${encodeURIComponent(groupCode)}`;
    if (roundNumber !== undefined) url += `&roundNumber=${roundNumber}`;
    return api.get<import('../types').MatchSummary[]>(url).then((res) => res.data);
  },
  getRoundMatches: (id: number, roundNumber: number) =>
    api.get<import('../types').MatchSummary[]>(`/competitions/${id}/round-matches?roundNumber=${roundNumber}`).then((res) => res.data),
};

// Active Countries
export const activeCountriesApi = {
  list: () => api.get<ActiveCountry[]>('/active-countries').then((res) => res.data),
  create: (data: CreateActiveCountryRequest) => api.post<ActiveCountry>('/active-countries', data).then((res) => res.data),
  delete: (id: number) => api.delete(`/active-countries/${id}`).then((res) => res.data),
};

// Teams
export const teamsApi = {
  list: () => api.get<Team[]>('/teams').then((res) => res.data),
  listByCompetition: (competitionId: number) =>
    api.get<Team[]>(`/teams/by-competition/${competitionId}`).then((res) => res.data),
  withPlayers: () => api.get<Team[]>('/teams/with-players').then((res) => res.data),
  updatePtName: (id: number, ptName: string | null) =>
    api.patch<Team>(`/teams/${id}/pt-name`, { ptName }).then((res) => res.data),
};

export const teamPlayersApi = {
  list: (teamId: number) => api.get<TeamPlayer[]>(`/teams/${teamId}/players`).then((res) => res.data),
  updateName: (teamId: number, playerId: number, name: string) =>
    api.patch<TeamPlayer>(`/teams/${teamId}/players/${playerId}`, { name }).then((res) => res.data),
  delete: (teamId: number, playerId: number) =>
    api.delete(`/teams/${teamId}/players/${playerId}`).then((res) => res.data),
  sync: (teamId: number) =>
    api.post<{ success: boolean; playersSynced: number }>(`/teams/${teamId}/sync-players`).then((res) => res.data),
};

// Users
export const usersApi = {
  getDetails: (externalId: string) => api.get<User>(`/users/${externalId}/details`).then((res) => res.data),
  getStats: (externalId: string) => api.get<UserStats>(`/users/${externalId}/stats`).then((res) => res.data),
  getStatistics: (externalId: string) => api.get<any>(`/users/${externalId}/statistics`).then((res) => res.data),
  update: (externalId: string, data: UpdateUserRequest) => api.put<User>(`/users/${externalId}`, data).then((res) => res.data),
  /** Avatar sincronizado no backend (V053) — base64 data URL.
   *  Substitui o localStorage que só persistia no device atual. */
  getAvatar: (externalId: string) =>
    api.get<{ avatarUrl: string | null }>(`/users/${externalId}/avatar`).then((res) => res.data),
  updateAvatar: (externalId: string, avatarUrl: string | null) =>
    api.put(`/users/${externalId}/avatar`, { avatarUrl }).then((res) => res.data),
  // Management endpoints (Super Admin only)
  create: (data: import('../types').RegisterRequest) =>
    api.post<import('../types').User>('/users', data).then((res) => res.data),
  list: (page = 0, size = 10) =>
    api.get<UserPage>('/users', { params: { page, size } }).then((res) => res.data),
  /** Admin deleta outro user (requer SUPER_ADMIN) */
  delete: (externalId: string) => api.delete(`/users/${externalId}`).then((res) => res.data),
  /** User deleta a própria conta (LGPD + Data Safety) */
  deleteOwnAccount: () => api.delete(`/users/me`).then((res) => res.data),
  updateRole: (externalId: string, role: string) =>
    api.patch<User>(`/users/${externalId}/role`, { role }).then((res) => res.data),
  search: (q: string) =>
    api.get<Pick<User, 'externalId' | 'name' | 'email'>[]>('/users/search', { params: { q } }).then((res) => res.data),
};

// Invitations
export const invitationsApi = {
  listByPool: (poolId: number) => api.get<Invitation[]>(`/invitations/pool/${poolId}`).then((res) => res.data),
};

// Notification logs (dashboard admin) — endpoints já existentes no backend
export interface NotificationLogItem {
  id: number;
  type: string;
  title: string;
  body: string;
  platform: string; // ANDROID | IOS | WEB
  channel: string;  // APP | WEB
  success: boolean;
  sentAt: string;
  sentBy: string;   // SCHEDULER | <externalId do admin>
}
export interface NotificationLogsPage {
  content: NotificationLogItem[];
  totalElements: number;
  totalPages: number;
  page: number;
}
export interface NotificationStats {
  total: number;
  success: number;
  failure: number;
  byType: Record<string, number>;   // { MATCH_REMINDER: N, ACHIEVEMENT: N }
  byChannel: Record<string, number>; // { APP: N, WEB: N }
}
export const notificationLogsApi = {
  getLogs: (page = 0, size = 20) =>
    api.get<NotificationLogsPage>('/admin/notifications/logs', { params: { page, size } }).then((res) => res.data),
  getStats: () =>
    api.get<NotificationStats>('/admin/notifications/stats').then((res) => res.data),
};

// Sino de notificações in-app do usuário (paridade com o App — CLAUDE.md item 10)
export interface UserNotificationItem {
  id: number;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  read: boolean;
  createdAt: string; // ISO-8601 com Z
}
export interface UserNotificationsPage {
  content: UserNotificationItem[];
  totalElements: number;
  totalPages: number;
  page: number;
}
export const userNotificationsApi = {
  list: (page = 0, size = 20) =>
    api.get<UserNotificationsPage>('/user-notifications', { params: { page, size } }).then((res) => res.data),
  unreadCount: () =>
    api.get<{ count: number }>('/user-notifications/unread-count').then((res) => res.data),
  readAll: () =>
    api.patch('/user-notifications/read-all').then((res) => res.data),
  delete: (id: number) =>
    api.delete(`/user-notifications/${id}`).then((res) => res.data),
};

// Banners de Anúncio (público — sem auth)
export const bannersApi = {
  getActive: () => api.get<BannersResponse>('/banners/active').then((res) => res.data),
};

// Push Notifications Config
export const pushConfigApi = {
  getAdvanceMinutes: () => api.get<number>('/push/config/advance-minutes').then((res) => res.data),
};

// Bug Reports
export const bugReportsApi = {
  create: (data: import('../types').BugReportRequest) =>
    api.post('/bug-reports', data).then((res) => res.data),
};

// AdSense
export const adsenseApi = {
  getConfig: () =>
    api.get<import('../types').AdsenseConfig>('/admin/config/adsense').then((res) => res.data),
  getPublicConfig: () =>
    api.get<import('../types').AdsenseConfig>('/admin/config/adsense-public').then((res) => res.data),
};

// Premium
export const premiumApi = {
  listPlans: () =>
    api.get<import('../types').PremiumPlan[]>('/premium/plans').then((res) => res.data),
  listAllPlans: () =>
    api.get<import('../types').PremiumPlan[]>('/premium/admin/plans').then((res) => res.data),
  getStatus: (userExternalId: string) =>
    api.get<import('../types').PremiumStatus>('/premium/status', { params: { userExternalId } }).then((res) => res.data),
  grantPremium: (data: { userExternalId: string; planId: number; days: number; paymentReference: string | null }) =>
    api.post('/premium/admin/grant', data).then((res) => res.data),
  createPlan: (data: {
    name: string;
    description: string | null;
    priceCents: number;
    periodDays: number;
    nameEn?: string | null;
    nameEs?: string | null;
    descriptionEn?: string | null;
    descriptionEs?: string | null;
  }) => api.post('/premium/admin/plans', data).then((res) => res.data),
  updatePlan: (
    id: number,
    data: {
      name: string;
      description: string | null;
      priceCents: number;
      periodDays: number;
      nameEn?: string | null;
      nameEs?: string | null;
      descriptionEn?: string | null;
      descriptionEs?: string | null;
    },
  ) => api.put<import('../types').PremiumPlan>(`/premium/admin/plans/${id}`, data).then((res) => res.data),
  deactivatePlan: (id: number) => api.patch(`/premium/admin/plans/${id}/deactivate`),
  activatePlan: (id: number) => api.patch(`/premium/admin/plans/${id}/activate`),
  setDefaultPlan: (id: number) => api.patch<import('../types').PremiumPlan>(`/premium/admin/plans/${id}/set-default`).then((res) => res.data),
  revokeSubscription: (userExternalId: string) =>
    api.delete(`/premium/admin/subscriptions/${userExternalId}`),
  listSubscriptions: (params?: { page?: number; size?: number; status?: string; origin?: string }) =>
    api.get<import('../types').SubscriptionPage>('/premium/admin/subscriptions', { params: { page: 0, size: 20, ...params } }).then((res) => res.data),

  // ── Pricing regional (Stripe) ──────────────────────────────────────────
  /**
   * Resolve o preço aplicável ao país do user. Devolve 204 (undefined) se
   * o plano não tiver nenhum preço configurado pra região + sem default.
   */
  getPreferredPrice: (planId: number, country?: string) =>
    api.get<import('../types').RegionalPrice>(`/premium/plans/${planId}/preferred-price`, {
      params: country ? { country } : undefined,
    }).then((res) => res.status === 204 ? null : res.data).catch(() => null),
  listRegionalPrices: (planId: number) =>
    api.get<import('../types').RegionalPrice[]>(`/premium/admin/plans/${planId}/prices`).then((res) => res.data),
  createRegionalPrice: (planId: number, data: {
    currency: string;
    priceCents: number;
    countryCodes: string[];
    isDefault?: boolean;
    active?: boolean;
  }) =>
    api.post<import('../types').RegionalPrice>(`/premium/admin/plans/${planId}/prices`, data).then((res) => res.data),
  updateRegionalPrice: (priceId: number, data: {
    currency?: string;
    priceCents?: number;
    countryCodes?: string[];
    isDefault?: boolean;
    active?: boolean;
  }) =>
    api.put<import('../types').RegionalPrice>(`/premium/admin/prices/${priceId}`, data).then((res) => res.data),
  deleteRegionalPrice: (priceId: number) =>
    api.delete(`/premium/admin/prices/${priceId}`),

  /**
   * Provisiona stripe_price_id pra um preço regional que ainda não tem.
   * Idempotente — se já tiver ID, devolve o mesmo. Cobre o caso da seed
   * V128 que insere os 3 prices sem chamar Stripe.
   */
  syncStripePrice: (priceId: number) =>
    api.post<import('../types').RegionalPrice>(`/premium/admin/prices/${priceId}/sync-stripe`).then((res) => res.data),
};

// Payments (Asaas + Stripe)
export const paymentsApi = {
  subscribe: (data: {
    planId: number;
    creditCard?: {
      holderName: string;
      number: string;
      expiryMonth: string;
      expiryYear: string;
      ccv: string;
    } | null;
    couponCode?: string | null;
    postalCode?: string | null;
    addressNumber?: string | null;
    cpf?: string | null;
    phone?: string | null;
  }) =>
    api.post<import('../types').SubscriptionDetails>('/payments/subscribe', data).then((res) => res.data),
  /**
   * Assinatura via Stripe (web internacional). PaymentMethod já tokenizado
   * pelo Stripe Elements no browser; backend nunca recebe número de cartão.
   */
  subscribeStripe: (data: {
    planId: number;
    paymentMethodId: string;
    couponCode?: string | null;
    countryCode?: string | null;
  }) =>
    api.post<import('../types').SubscriptionDetails>('/payments/subscribe-stripe', data).then((res) => res.data),
  getSubscription: () =>
    api.get<import('../types').SubscriptionDetails>('/payments/subscription').then((res) => res.data),
  cancel: () =>
    api.delete('/payments/subscription'),
  disableRecurring: () =>
    api.patch('/payments/subscription/recurring'),
  enableRecurring: () =>
    api.patch('/payments/subscription/recurring/enable'),
  getHistory: () =>
    api.get<import('../types').SubscriptionDetails[]>('/payments/subscriptions/history').then((res) => res.data),
};

// Admin — notificações em massa
export const adminApi = {
  notifyAllUsers: (subject: string, content: string) =>
    api.post<{ message: string; count: number }>('/admin/notify-users', { subject, content }).then((res) => res.data),
};

// Sugestões de Competição
export const competitionSuggestionsApi = {
  create: (competitionName: string) =>
    api.post('/competition-suggestions', { competitionName }).then((res) => res.data),
};

// Conquistas
export const achievementsApi = {
  listAll: () =>
    api.get<import('../types').Achievement[]>('/achievements').then((res) => res.data),
  getUserAchievements: (userExternalId: string) =>
    api.get<import('../types').UserAchievementsResponse>(`/achievements/user/${userExternalId}`).then((res) => res.data),
};

// Cupons de Desconto
export const couponsApi = {
  validate: (code: string) =>
    api.get<{ code: string; discountType: string; discountValue: number; active: boolean }>(`/coupons/validate`, { params: { code } }).then((res) => res.data),
  listAll: () =>
    api.get<import('../types').CouponResponse[]>('/coupons').then((res) => res.data),
  create: (data: {
    code: string;
    discountType: 'PERCENT' | 'FIXED';
    discountValue: number;
    maxUses?: number | null;
    expiresAt?: string | null;
  }) =>
    api.post<import('../types').CouponResponse>('/coupons', data).then((res) => res.data),
  deactivate: (id: number) =>
    api.patch<import('../types').CouponResponse>(`/coupons/${id}/deactivate`).then((res) => res.data),
  activate: (id: number) =>
    api.patch<import('../types').CouponResponse>(`/coupons/${id}/activate`).then((res) => res.data),
  remove: (id: number) =>
    api.delete(`/coupons/${id}`),
};

// Competition Announcements (Destaques)
export const announcementsApi = {
  listActive: () =>
    api.get<import('../types').CompetitionAnnouncement[]>('/competition-announcements/active').then((res) => res.data),
  listAll: () =>
    api.get<import('../types').CompetitionAnnouncement[]>('/competition-announcements').then((res) => res.data),
  upsert: (data: {
    competitionId: number;
    title: string;
    description: string;
    titleEn?: string | null;
    titleEs?: string | null;
    descriptionEn?: string | null;
    descriptionEs?: string | null;
    titleFontSize?: number;
    descriptionFontSize?: number;
    titleColor?: string;
    descriptionColor?: string;
    enabled: boolean;
  }) =>
    api.post<import('../types').CompetitionAnnouncement>('/competition-announcements', data).then((res) => res.data),
  remove: (id: number) =>
    api.delete(`/competition-announcements/${id}`),
};

// Simulador de Copas (público, sem auth)
export const simulatorApi = {
  getBracket: (externalId: number): Promise<SimulatorBracket> =>
    api.get<SimulatorBracket>(`/simulator/competitions/${externalId}/bracket`).then(res => res.data),
};

// Admin — Payment Events (webhooks Stripe + Asaas, SUPER_ADMIN only)
export const paymentEventsAdminApi = {
  list: (params?: {
    page?: number;
    size?: number;
    eventType?: string;
    processed?: boolean;
    gateway?: 'stripe' | 'asaas';
  }) =>
    api.get<{
      content: import('../types').PaymentEventListItem[];
      totalElements: number;
      totalPages: number;
      page: number;
      size: number;
    }>('/admin/payment-events', { params: { page: 0, size: 20, ...params } }).then((res) => res.data),

  detail: (id: number) =>
    api.get<import('../types').PaymentEventDetail>(`/admin/payment-events/${id}`).then((res) => res.data),

  stats: () =>
    api.get<{
      total_24h: number;
      failed_24h: number;
      success_rate_24h: number;
      total_all_time: number;
    }>('/admin/payment-events/stats').then((res) => res.data),
};

export default api;
